/**
 * Termly
 *
 * https://www.termly.io/
 *
 * Termly Event API: https://support.termly.io/hc/en-us/articles/30710442081553
 *
 * The termlyConsent event detail is an object with a categories array:
 * { categories: ["essential", "analytics", "advertising", "performance", ...], cookies: [...], uuid: "..." }
 *
 * Termly.getConsentState() returns: { essential: bool, analytics: bool, advertising: bool, performance: bool, social_networking: bool, unclassified: bool }
 *
 * The localStorage key termly_gtm_template_default_consents uses Google Consent Mode category names:
 * { analytics_storage: "granted"|"denied", ad_storage: "granted"|"denied", ... }
 */
export const termly = {

	name: "Termly",

	/**
	 * Get the Termly consent state.
	 *
	 * Reads from localStorage (GCM format) first, then falls back to Termly.getConsentState() (Termly category format).
	 *
	 * @returns {{statistics: boolean, marketing: boolean, preferences: boolean, necessary: boolean}|null}
	 */
	getConsent: () => {

		// Try localStorage first (uses Google Consent Mode category names)
		let consent = localStorage.getItem("termly_gtm_template_default_consents")

		if (consent) {

			pmw.console.log("Termly CMP consent detected (from localStorage)")

			consent = JSON.parse(consent)

			return {
				statistics : consent.analytics_storage !== "denied",
				marketing  : consent.ad_storage !== "denied",
				preferences: consent.functionality_storage !== "denied",
				necessary  : consent.security_storage !== "denied",
			}
		}

		// Fall back to Termly.getConsentState() if available
		if (window.Termly && typeof window.Termly.getConsentState === "function") {

			const state = window.Termly.getConsentState()

			if (state) {
				pmw.console.log("Termly CMP consent detected (from Termly.getConsentState())")

				return {
					statistics : !!state.analytics,
					marketing  : !!state.advertising,
					preferences: !!state.performance,
					necessary  : state.essential !== false,
				}
			}
		}

		return null
	},

	/**
	 * Termly Event Listeners
	 *
	 * The termlyConsent event is dispatched by Termly's resource-blocker script.
	 * e.detail is an object: { categories: ["essential", "analytics", ...], cookies: [...], uuid: "..." }
	 *
	 * Category mapping:
	 * statistics  = analytics
	 * marketing   = advertising
	 * preferences = performance
	 * necessary   = essential
	 */
	loadEventListeners: () => {

		document.addEventListener("termlyConsent", function (e) {

			const categories = e.detail?.categories

			if (!Array.isArray(categories)) {
				pmw.console.log("Termly consent event received but categories is not an array", e.detail)
				return
			}

			pmw.console.log("Termly consent event received", categories)

			if (
				categories.includes("analytics")
				&& categories.includes("advertising")
				&& categories.includes("performance")
			) {
				pmw.consent.api.acceptAll({source: "cmp"})
				return
			}

			if (
				!categories.includes("analytics")
				&& !categories.includes("advertising")
				&& !categories.includes("performance")
			) {
				pmw.consent.api.revokeAll({source: "cmp"})
				return
			}

			pmw.consent.api.updateSelectively({
				statistics : categories.includes("analytics"),
				marketing  : categories.includes("advertising"),
				preferences: categories.includes("performance"),
				necessary  : categories.includes("essential"),
				source     : "cmp",
			})

		}, true)
	},
}
